import { createContext, type ReactNode, useCallback, useContext, useEffect, useState } from "react";

export type ThemeChoice = "system" | "light" | "dark";
const STORAGE_KEY = "bt-theme";

const ThemeContext = createContext<{ theme: ThemeChoice; setTheme: (theme: ThemeChoice) => void }>({
  theme: "system",
  setTheme: () => {},
});

function readStored(): ThemeChoice {
  try {
    const value = localStorage.getItem(STORAGE_KEY);
    if (value === "light" || value === "dark") return value;
  } catch {
    // Storage unavailable: follow the system.
  }
  return "system";
}

let themeColors: { light: string; dark: string } | undefined;

/**
 * index.html has a theme-color entry per system scheme, for Android's status bar and an installed
 * app's title bar. A theme chosen in the app wins over the system's, so both entries get the
 * colour of the theme shown.
 */
function applyThemeColor(dark: boolean) {
  const tags = Array.from(document.querySelectorAll<HTMLMetaElement>('meta[name="theme-color"]'));
  themeColors ??= {
    light: tags.find((tag) => tag.media.includes("light"))?.content ?? "",
    dark: tags.find((tag) => tag.media.includes("dark"))?.content ?? "",
  };
  for (const tag of tags) tag.content = dark ? themeColors.dark : themeColors.light;
}

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [theme, setThemeState] = useState<ThemeChoice>(readStored);

  useEffect(() => {
    const media = matchMedia("(prefers-color-scheme: dark)");
    const apply = () => {
      const dark = theme === "dark" || (theme === "system" && media.matches);
      document.documentElement.classList.toggle("dark", dark);
      applyThemeColor(dark);
    };
    apply();
    media.addEventListener("change", apply);
    return () => media.removeEventListener("change", apply);
  }, [theme]);

  const setTheme = useCallback((next: ThemeChoice) => {
    try {
      if (next === "system") localStorage.removeItem(STORAGE_KEY);
      else localStorage.setItem(STORAGE_KEY, next);
    } catch {
      // Not persisted.
    }
    setThemeState(next);
  }, []);

  return <ThemeContext value={{ theme, setTheme }}>{children}</ThemeContext>;
}

export function useTheme() {
  return useContext(ThemeContext);
}
